import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { playlistApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function PublicPlaylistPage() {
  const { token } = useParams();

  const [playlist, setPlaylist]         = useState(null);
  const [cards, setCards]               = useState([]);
  const [sortedCards, setSortedCards]   = useState([]);
  const [creatorPrefs, setCreatorPrefs] = useState({});
  const [index, setIndex]               = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [started, setStarted]           = useState(false);
  const [randomOrder, setRandomOrder]   = useState(false);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  useEffect(() => {
    playlistApi.getPublic(token)
      .then(data => {
        const pl    = data.playlist;
        const prefs = data.creatorPrefs || {};
        const ordered = data.cards || [];
        setPlaylist(pl);
        setSortedCards(ordered);
        setCards(ordered);
        setCreatorPrefs(prefs);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Reset solution when card changes
  useEffect(() => { setShowSolution(false); }, [index]);

  // Keyboard navigation (only while reviewing)
  useEffect(() => {
    if (!started) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  setIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex(i => Math.min(cards.length - 1, i + 1));
      if (e.key === ' ')          { e.preventDefault(); setShowSolution(s => !s); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [started, cards.length]);

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  if (error)   return <div className="alert alert-danger mt-4">This playlist is unavailable or the link is invalid.</div>;

  // ── Start screen ────────────────────────────────────────────────────────────
  if (!started) {
    return (
      <div className="row justify-content-center mt-5">
        <div className="col-md-5 text-center">
          <h2 className="mb-2">{playlist?.title}</h2>
          <p className="text-muted small mb-4">Shared playlist</p>
          <p className="text-muted mb-1">
            {cards.length} card{cards.length !== 1 ? 's' : ''}
          </p>
          {playlist?.allow_random && (
            <div className="form-check d-inline-flex align-items-center gap-2 mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="randomOrderCheck"
                checked={randomOrder}
                onChange={e => setRandomOrder(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="randomOrderCheck">
                Show in random order
              </label>
            </div>
          )}
          {cards.length === 0 ? (
            <div className="alert alert-warning">This playlist has no cards yet.</div>
          ) : (
            <p>
            <button
              className="btn btn-primary btn-lg"
              onClick={() => {
                setCards(randomOrder ? shuffle(sortedCards) : sortedCards);
                setIndex(0);
                setStarted(true);
              }}
            >
              Start Reviewing
            </button>
            </p>
          )}
        </div>
      </div>
    );
  }

  const card = cards[index];
  const cardPrefs = {
    ...creatorPrefs,
    diagramFlipped:              card.flipped            ?? creatorPrefs.diagramFlipped,
    diagramShowSideToMoveDisc:   card.show_side_to_move  ?? creatorPrefs.diagramShowSideToMoveDisc,
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="fw-semibold text-muted">{playlist?.title}</span>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">
            Card {index + 1} of {cards.length}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="progress" style={{ height: 6 }}>
          <div
            className="progress-bar"
            style={{ width: `${((index + 1) / cards.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">{card.name}</h4>
              {card.to_move && (
                <span className={`badge ${card.to_move === 'W' ? 'bg-light text-dark border' : 'bg-dark'}`}>
                  {card.to_move === 'W' ? 'White to move' : 'Black to move'}
                </span>
              )}
            </div>

            <div className="card-body">
              {card.fen && (
                <div className="mb-4 text-center">
                  <ChessDiagram fen={card.fen} prefs={cardPrefs} size={320} />
                </div>
              )}

              {card.problem && (
                <div className="mb-4">
                  <h6 className="text-muted text-uppercase small mb-2">Problem</h6>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
                </div>
              )}

              {/* Solution */}
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h6 className="text-muted text-uppercase small mb-0">Solution</h6>
                  <button
                    className={`btn btn-sm ${showSolution ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setShowSolution(s => !s)}
                  >
                    {showSolution ? 'Hide Solution' : 'Show Solution'}
                  </button>
                </div>
                {showSolution ? (
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {card.solution || <em className="text-muted">No solution provided.</em>}
                  </p>
                ) : (
                  <div className="text-muted fst-italic small">Solution hidden — click to reveal</div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setIndex(i => Math.max(0, i - 1))}
              disabled={index === 0}
            >
              ← Previous
            </button>
            <span className="text-muted small">← → to navigate · Space to toggle solution</span>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setIndex(i => Math.min(cards.length - 1, i + 1))}
              disabled={index === cards.length - 1}
            >
              Next →
            </button>
          </div>

          {index === cards.length - 1 && (
            <div className="text-center mt-4">
              <div className="alert alert-success">You've reached the end of this playlist!</div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setCards(randomOrder ? shuffle(sortedCards) : sortedCards);
                  setIndex(0);
                  setShowSolution(false);
                }}
              >
                Start Over
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
